/**
 * Security Framework Utilities
 * 
 * Helper functions for the Security Framework component including:
 * - CSV parsing for framework import
 * - Framework color mapping
 * - Framework type utilities
 * 
 * @module SecurityFrameworkUtils
 */

/**
 * Parse CSV text into framework rules
 * Expected format: "Rule Name;Rule Description" (semicolon-separated)
 * 
 * @param {string} csvText - CSV text content
 * @returns {Array} - Array of rule objects
 * 
 * @example
 * const csvText = "Access Control;Implement access controls\nEncryption;Use strong encryption";
 * const rules = parseCSV(csvText);
 * // Returns: [
 * //   { id: 'custom-abc123', name: 'Access Control', description: 'Implement access controls', is_predefined: false },
 * //   { id: 'custom-def456', name: 'Encryption', description: 'Use strong encryption', is_predefined: false }
 * // ]
 */
export const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    return lines
        .filter(line => line.trim()) // Remove empty lines
        .map(line => {
            const [name, description] = line.split(';').map(s => s.trim());
            return {
                id: `custom-${Math.random().toString(36).substr(2, 9)}`,
                name,
                description,
                is_predefined: false
            };
        });
};

/**
 * Get framework color based on type
 * 
 * @param {string} type - Framework type (cis, nist, iso27002, anssi)
 * @returns {string} - Hex color code
 * 
 * @example
 * getFrameworkColor('cis')      // Returns: '#3B82F6' (Blue)
 * getFrameworkColor('nist')     // Returns: '#10B981' (Green)
 * getFrameworkColor('iso27002') // Returns: '#F59E0B' (Yellow)
 * getFrameworkColor('anssi')    // Returns: '#EF4444' (Red)
 * getFrameworkColor('custom')   // Returns: '#6B7280' (Gray)
 */
export const getFrameworkColor = (type) => {
    switch (type) {
        case 'cis':
            return '#3B82F6'; // Blue
        case 'nist':
            return '#10B981'; // Green
        case 'iso27002':
            return '#F59E0B'; // Yellow
        case 'anssi':
            return '#EF4444'; // Red
        default:
            return '#6B7280'; // Gray
    }
};

/**
 * Framework color palette
 * Maps framework types to their color schemes
 */
export const FRAMEWORK_COLORS = {
    cis: {
        primary: '#3B82F6',
        light: '#DBEAFE',
        dark: '#1E40AF',
        name: 'Blue'
    },
    nist: {
        primary: '#10B981',
        light: '#D1FAE5',
        dark: '#047857',
        name: 'Green'
    },
    iso27002: {
        primary: '#F59E0B',
        light: '#FEF3C7',
        dark: '#B45309',
        name: 'Yellow'
    },
    anssi: {
        primary: '#EF4444',
        light: '#FEE2E2',
        dark: '#B91C1C',
        name: 'Red'
    },
    default: {
        primary: '#6B7280',
        light: '#F3F4F6',
        dark: '#374151',
        name: 'Gray'
    }
};

/**
 * Get framework display name
 * 
 * @param {string} type - Framework type
 * @returns {string} - Human-readable framework name
 */
export const getFrameworkDisplayName = (type) => {
    const names = {
        cis: 'CIS Controls',
        nist: 'NIST Cybersecurity Framework',
        iso27002: 'ISO/IEC 27002',
        anssi: 'ANSSI Cybersecurity Guide'
    };
    return names[type] || 'Custom Framework';
};

/**
 * Validate CSV format
 * 
 * @param {string} csvText - CSV text to validate
 * @returns {Object} - { valid: boolean, errors: Array<string> }
 */
export const validateCSV = (csvText) => {
    const errors = [];

    if (!csvText || csvText.trim() === '') {
        errors.push('CSV content is empty');
        return { valid: false, errors };
    }

    const lines = csvText.split('\n').filter(line => line.trim());

    if (lines.length === 0) {
        errors.push('No valid lines found in CSV');
        return { valid: false, errors };
    }

    lines.forEach((line, index) => {
        const parts = line.split(';');
        if (parts.length < 2) {
            errors.push(`Line ${index + 1}: Missing description (expected format: "Name;Description")`);
        }
        if (!parts[0] || !parts[0].trim()) {
            errors.push(`Line ${index + 1}: Rule name is empty`);
        }
    });

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Generate unique framework ID
 * 
 * @param {string} name - Framework name
 * @returns {string} - Unique framework ID
 */
export const generateFrameworkId = (name) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 5);
    const cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '-').substr(0, 20);
    return `custom-${cleanName}-${timestamp}-${random}`;
};

/**
 * Clean framework name for display
 * Removes language indicators and version numbers
 * 
 * @param {string} name - Framework name
 * @returns {string} - Cleaned name
 * 
 * @example
 * cleanFrameworkName("ISO 27002 (FR) V2.0") // Returns: "ISO 27002"
 */
export const cleanFrameworkName = (name) => {
    if (!name) return '';
    return name
        .replace(/\s*\([A-Z]{2}\)\s*/gi, '') // Remove language indicators like (FR), (EN)
        .replace(/\s*V\d+\.\d+\s*/gi, '')     // Remove version numbers like V2.0
        .replace(/\s*\(.*\)\s*/gi, '')        // Remove any other parenthetical content
        .trim();
};

// Export all utilities as default object
export default {
    parseCSV,
    getFrameworkColor,
    FRAMEWORK_COLORS,
    getFrameworkDisplayName,
    validateCSV,
    generateFrameworkId,
    cleanFrameworkName
};
